/**
 * The touch feel of the level camera (QoL C1, C2): a pinch zooms about the point between the fingers and eases to the
 * fingers' zoom instead of stepping, and a flicked pan carries on and slows to a stop. Pure numbers — no three.js, no
 * DOM — so the rules are unit-tested; render/isoCamera.ts applies them to the view, input/gestures.ts measures the
 * release.
 */
export const CAMERA_FEEL = {
  /** A pinch eases to the fingers' zoom with this time constant (s): ~3 frames, so it tracks the fingers without steps. */
  ZoomEaseSec: 0.05,
  /** The eased zoom lands on its goal within this share of it. */
  ZoomSettle: 0.002,
  /** The release velocity is the finger's travel over the last this-many ms before it lifted. */
  FlingWindowMs: 90,
  /** A finger that rested this long (ms) before lifting throws nothing: the player put the map down. */
  FlingRestMs: 60,
  /** Slower than this (px/s) a release is a drop, not a flick. */
  FlingMinPxPerSec: 250,
  /** A flick is capped at this speed (px/s): a wild throw glides, it does not jump the map. */
  FlingMaxPxPerSec: 4000,
  /** Momentum decays with this time constant (s): a third of the speed is left after ~0.35 s, like a map app. */
  FlingDecaySec: 0.32,
  /** Momentum stops below this ground speed (world units, i.e. cells, per second). */
  FlingStopCellsPerSec: 0.08,
  /** The screen-to-ground scale of a flick is measured over this much of its travel (s). */
  FlingProbeSec: 0.05,
} as const;

/** One finger position during a drag, with its time in ms. */
export interface DragSample { readonly x: number; readonly y: number; readonly t: number }

/**
 * The finger's velocity (px/s) as it lifted at `upT`: its travel over the last FlingWindowMs of `samples` (oldest
 * first). Zero for a finger that rested before lifting, or for a release slower than FlingMinPxPerSec; capped at
 * FlingMaxPxPerSec. Over a window rather than the last two samples, so a jittered 60 Hz trace gives a steady answer.
 */
export function releaseVelocity(samples: readonly DragSample[], upT: number): { vx: number; vy: number } {
  const none = { vx: 0, vy: 0 };
  if (samples.length < 2) return none;
  const last = samples[samples.length - 1];
  if (upT - last.t > CAMERA_FEEL.FlingRestMs) return none;
  let first = last;
  for (let i = samples.length - 1; i >= 0 && last.t - samples[i].t <= CAMERA_FEEL.FlingWindowMs; i--) first = samples[i];
  const dt = (last.t - first.t) / 1000;
  if (dt <= 0) return none;
  let vx = (last.x - first.x) / dt, vy = (last.y - first.y) / dt;
  const speed = Math.hypot(vx, vy);
  if (speed < CAMERA_FEEL.FlingMinPxPerSec) return none;
  if (speed > CAMERA_FEEL.FlingMaxPxPerSec) { const k = CAMERA_FEEL.FlingMaxPxPerSec / speed; vx *= k; vy *= k; }
  return { vx, vy };
}

/** `value` eased toward `goal` over `dt` s with time constant `tau` (frame-rate independent). */
export function easeToward(value: number, goal: number, dt: number, tau: number): number {
  return goal + (value - goal) * Math.exp(-Math.max(0, dt) / tau);
}

/** A momentum speed after `dt` s of decay. */
export function decayed(speed: number, dt: number): number { return speed * Math.exp(-Math.max(0, dt) / CAMERA_FEEL.FlingDecaySec); }

/** What CameraTouch needs of the camera (IsoCamera supplies it; tests use a flat fake). */
export interface TouchHost {
  readonly zoom: number;
  /** Sets the zoom (clamped to the camera's range) and re-applies the view, clamped to the map edge. */
  setZoom(zoom: number): void;
  /** Where a viewport point meets the ground, or null above the horizon. */
  groundAt(px: number, py: number): { x: number; z: number } | null;
  /** Moves the look point by a ground offset, clamped like a drag; returns the offset it really moved. */
  panBy(dx: number, dz: number): { x: number; z: number };
  /** The viewport point a flick's scale is measured at (the middle of the uncovered band). */
  readonly probe: { x: number; y: number };
}

/**
 * The eased pinch and the flick's momentum on one camera. A pinch sets a zoom goal about an anchor (the point between
 * the fingers); each frame the zoom eases toward it and the pan is corrected so the ground under the anchor stays
 * under it. A flick sets a ground velocity that decays; the pan's edge clamp stops it at the map's edge.
 */
export class CameraTouch {
  private zoomGoal = NaN;
  private anchor: { x: number; y: number } | null = null;
  private vx = 0;
  private vz = 0;

  constructor(private readonly host: TouchHost) {}

  /** Whether a zoom ease or a glide is under way. */
  get busy(): boolean { return !Number.isNaN(this.zoomGoal) || this.vx !== 0 || this.vz !== 0; }

  /** A pinch step: the zoom goal divided by `scale` (fingers apart → closer), within [min, max], about (px, py). */
  pinch(scale: number, px: number, py: number, min: number, max: number): void {
    this.vx = this.vz = 0;
    const from = Number.isNaN(this.zoomGoal) ? this.host.zoom : this.zoomGoal;
    this.zoomGoal = Math.min(max, Math.max(min, from / scale));
    this.anchor = { x: px, y: py };
  }

  /** A flick released at (vxPx, vyPx) screen px/s: the ground carries on under the finger's last speed. */
  fling(vxPx: number, vyPx: number): void {
    const p = this.host.probe, h = CAMERA_FEEL.FlingProbeSec;
    const a = this.host.groundAt(p.x, p.y), b = this.host.groundAt(p.x + vxPx * h, p.y + vyPx * h);
    if (a === null || b === null) return;
    // the ground under the finger followed it, so the look point moves the other way
    this.vx = -(b.x - a.x) / h;
    this.vz = -(b.z - a.z) / h;
  }

  /** The player took the camera again (a touch, a glide): everything in flight stops where it is. */
  stop(): void { this.zoomGoal = NaN; this.anchor = null; this.vx = this.vz = 0; }

  /** One frame; returns whether the view moved. */
  step(dt: number): boolean {
    let moved = false;
    if (!Number.isNaN(this.zoomGoal)) {
      const goal = this.zoomGoal, now = this.host.zoom;
      const next = Math.abs(now - goal) <= goal * CAMERA_FEEL.ZoomSettle ? goal : easeToward(now, goal, dt, CAMERA_FEEL.ZoomEaseSec);
      this.zoomAbout(next);
      if (next === goal) { this.zoomGoal = NaN; this.anchor = null; }
      moved = true;
    }
    if (this.vx !== 0 || this.vz !== 0) {
      const want = { x: this.vx * dt, z: this.vz * dt };
      const got = this.host.panBy(want.x, want.z);
      // the edge clamp ate the move along an axis: the glide stops on that axis
      if (Math.abs(got.x) < Math.abs(want.x) * 0.5) this.vx = 0;
      if (Math.abs(got.z) < Math.abs(want.z) * 0.5) this.vz = 0;
      this.vx = decayed(this.vx, dt); this.vz = decayed(this.vz, dt);
      if (Math.hypot(this.vx, this.vz) < CAMERA_FEEL.FlingStopCellsPerSec) this.vx = this.vz = 0;
      moved = true;
    }
    return moved;
  }

  /** Sets the zoom keeping the ground under the anchor where it was on screen. */
  private zoomAbout(zoom: number): void {
    const a = this.anchor, before = a ? this.host.groundAt(a.x, a.y) : null;
    this.host.setZoom(zoom);
    if (!a || !before) return;
    const after = this.host.groundAt(a.x, a.y);
    if (after) this.host.panBy(before.x - after.x, before.z - after.z);
  }
}
